import type {
  DraggedItem,
  FileMeta,
  FileUploadStatus,
  FileWithMeta,
  GetUploadParams,
  OnChangeStatus,
  UploadParams,
} from './types';

export interface FileUploadOptions {
  accept: string;
  minSizeBytes: number;
  maxSizeBytes: number;
  maxFiles: number;
  getUploadParams?: GetUploadParams;
  onChangeStatus?: OnChangeStatus;
}

// Firefox before 53 reports this MIME type for every dragged file, so it always passes the accept check.
const MOZ_FILE = 'application/x-moz-file';

/**
 * Matches a file against an `<input accept>` string. A dragged `DataTransferItem` has no name,
 * so it passes every extension rule.
 */
export const accepts = (file: { name?: string; type?: string }, accept: string): boolean => {
  if (!accept || accept === '*') return true;

  const mimeType = file.type || '';
  const baseMimeType = mimeType.replace(/\/.*$/, '');

  return accept
    .split(',')
    .map(t => t.trim())
    .some(type => {
      if (type.charAt(0) === '.') {
        return file.name === undefined || file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      if (type.endsWith('/*')) {
        return baseMimeType === type.replace(/\/.*$/, '');
      }
      return mimeType === type;
    });
};

export const isRejectedType = (file: DraggedItem, accept: string): boolean =>
  file.type !== MOZ_FILE && !accepts(file as File, accept);

/**
 * Reads files as the browser hands them over, without inferring a MIME type from the extension:
 * every accept rule and every caller's type check sees `file.type` exactly as the browser set it.
 */
export const getFilesFromEvent = (event: unknown): DraggedItem[] => {
  const evt = event as { dataTransfer?: DataTransfer; target?: { files?: FileList | null } };
  let items: ArrayLike<DraggedItem> | null | undefined = null;

  if (evt.dataTransfer) {
    const dt = evt.dataTransfer;
    // Only a drop event can read DataTransfer.files; drag events see the items.
    if (dt.files && dt.files.length) {
      items = dt.files;
    } else if (dt.items && dt.items.length) {
      items = dt.items;
    }
  } else if (evt.target && evt.target.files) {
    items = evt.target.files;
  }

  return Array.prototype.slice.call(items ?? []);
};

const PREVIEW_TIMEOUT_MS = 1000;
const SCROLL_DELAY_MS = 150;

/**
 * Holds the files a dropzone has taken in and drives each one through validation, preview and an
 * XMLHttpRequest upload. Each `FileWithMeta` is mutated in place, so a caller that kept one from
 * an earlier `onChangeStatus` call sees its current status and can still `remove()` it.
 */
export class FileUploadStore {
  options: FileUploadOptions;

  private files: FileWithMeta[] = [];

  private snapshot: FileWithMeta[] = [];

  private listeners = new Set<() => void>();

  constructor(options: FileUploadOptions) {
    this.options = options;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): FileWithMeta[] => this.snapshot;

  addFiles = (files: File[], scrollContainer?: HTMLElement | null): void => {
    const batch = new Date().getTime();
    files.forEach((file, i) => {
      void this.handleFile(file, `${batch}-${i}`);
    });
    if (scrollContainer) {
      setTimeout(
        () => scrollContainer.scroll?.({ top: scrollContainer.scrollHeight, behavior: 'smooth' }),
        SCROLL_DELAY_MS,
      );
    }
  };

  /** Aborts every upload in flight. */
  cancelAll = (): void => {
    for (const fileWithMeta of [...this.files]) this.cancel(fileWithMeta);
  };

  private emit(): void {
    this.snapshot = [...this.files];
    this.listeners.forEach(listener => listener());
  }

  // Changes land on the meta object a caller may already hold.
  private static patch(fileWithMeta: FileWithMeta, meta: Partial<FileMeta>): void {
    Object.assign(fileWithMeta.meta, meta);
  }

  private setStatus(fileWithMeta: FileWithMeta, status: FileUploadStatus): void {
    FileUploadStore.patch(fileWithMeta, { status });
    this.options.onChangeStatus?.(fileWithMeta, status, this.files);
  }

  private cancel(fileWithMeta: FileWithMeta): void {
    if (fileWithMeta.meta.status !== 'uploading') return;
    // Set first, so the abort's readystatechange does not report exception_upload.
    FileUploadStore.patch(fileWithMeta, { status: 'aborted' });
    fileWithMeta.xhr?.abort();
    this.setStatus(fileWithMeta, 'aborted');
    this.emit();
  }

  private remove(fileWithMeta: FileWithMeta): void {
    const index = this.files.indexOf(fileWithMeta);
    if (index === -1) return;
    URL.revokeObjectURL(fileWithMeta.meta.previewUrl || '');
    this.setStatus(fileWithMeta, 'removed');
    this.files.splice(index, 1);
    this.emit();
  }

  private restart(fileWithMeta: FileWithMeta): void {
    if (!this.options.getUploadParams) return;
    this.setStatus(fileWithMeta, 'restarted');
    FileUploadStore.patch(fileWithMeta, { percent: 0 });
    this.setStatus(fileWithMeta, 'getting_upload_params');
    this.emit();
    void this.upload(fileWithMeta);
  }

  private async handleFile(file: File, id: string): Promise<void> {
    const { name, size, type, lastModified } = file;
    const { accept, minSizeBytes, maxSizeBytes, maxFiles, getUploadParams } = this.options;

    const meta = {
      name,
      size,
      type,
      lastModifiedDate: lastModified && new Date(lastModified).toISOString(),
      uploadedDate: new Date().toISOString(),
      percent: 0,
      id,
    } as FileMeta;
    const rejected = { file, meta } as FileWithMeta;

    if (isRejectedType(file, accept)) {
      this.setStatus(rejected, 'rejected_file_type');
      return;
    }
    if (this.files.length >= maxFiles) {
      this.setStatus(rejected, 'rejected_max_files');
      return;
    }

    const fileWithMeta: FileWithMeta = {
      file,
      meta,
      cancel: () => this.cancel(fileWithMeta),
      remove: () => this.remove(fileWithMeta),
      restart: () => this.restart(fileWithMeta),
    };
    this.files.push(fileWithMeta);
    this.setStatus(fileWithMeta, 'preparing');
    this.emit();

    if (size < minSizeBytes || size > maxSizeBytes) {
      this.setStatus(fileWithMeta, 'error_file_size');
      this.emit();
      return;
    }

    await this.generatePreview(fileWithMeta);

    if (getUploadParams) {
      void this.upload(fileWithMeta);
      this.setStatus(fileWithMeta, 'getting_upload_params');
    } else {
      this.setStatus(fileWithMeta, 'done');
    }
    this.emit();
  }

  private async generatePreview(fileWithMeta: FileWithMeta): Promise<void> {
    const {
      meta: { type },
      file,
    } = fileWithMeta;
    const isImage = type.startsWith('image/');
    const isAudio = type.startsWith('audio/');
    const isVideo = type.startsWith('video/');
    if (!isImage && !isAudio && !isVideo) return;

    const objectUrl = URL.createObjectURL(file);

    const loaded = (element: HTMLImageElement | HTMLMediaElement) =>
      Promise.race([
        new Promise(resolve => {
          element.addEventListener(element instanceof HTMLImageElement ? 'load' : 'loadedmetadata', resolve, {
            once: true,
          });
        }),
        new Promise((_, reject) => {
          setTimeout(reject, PREVIEW_TIMEOUT_MS);
        }),
      ]);

    try {
      if (isImage) {
        const img = new Image();
        img.src = objectUrl;
        FileUploadStore.patch(fileWithMeta, { previewUrl: objectUrl });
        await loaded(img);
        FileUploadStore.patch(fileWithMeta, { width: img.width, height: img.height });
      }

      if (isAudio) {
        const audio = new Audio();
        audio.src = objectUrl;
        await loaded(audio);
        FileUploadStore.patch(fileWithMeta, { duration: audio.duration });
      }

      if (isVideo) {
        const video = document.createElement('video');
        video.src = objectUrl;
        await loaded(video);
        FileUploadStore.patch(fileWithMeta, {
          duration: video.duration,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
        });
      }
      if (!isImage) URL.revokeObjectURL(objectUrl);
    } catch {
      // A preview that does not load in time keeps its URL on the meta but frees the object URL.
      URL.revokeObjectURL(objectUrl);
    }
    this.emit();
  }

  private async upload(fileWithMeta: FileWithMeta): Promise<void> {
    const { getUploadParams } = this.options;
    if (!getUploadParams) return;

    let params: UploadParams | null = null;
    try {
      params = await getUploadParams(fileWithMeta);
    } catch (e) {
      // oxlint-disable-next-line no-console -- the file stays in getting_upload_params; this is its only trace
      console.error('Error Upload Params', (e as Error).stack);
    }
    if (params === null) return;
    const { url, method = 'POST', body, fields = {}, headers = {} } = params;

    if (!url) {
      this.setStatus(fileWithMeta, 'error_upload_params');
      this.emit();
      return;
    }

    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    xhr.open(method, url, true);

    for (const field of Object.keys(fields)) formData.append(field, fields[field]);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    for (const header of Object.keys(headers)) xhr.setRequestHeader(header, headers[header]);

    xhr.upload.addEventListener('progress', e => {
      FileUploadStore.patch(fileWithMeta, { percent: (e.loaded * 100.0) / e.total || 100 });
      this.emit();
    });

    xhr.addEventListener('readystatechange', () => {
      // HEADERS_RECEIVED and DONE are the only states that carry a status code.
      if (xhr.readyState !== 2 && xhr.readyState !== 4) return;

      if (xhr.status === 0 && fileWithMeta.meta.status !== 'aborted') {
        this.setStatus(fileWithMeta, 'exception_upload');
        this.emit();
      }

      if (xhr.status > 0 && xhr.status < 400) {
        FileUploadStore.patch(fileWithMeta, { percent: 100 });
        if (xhr.readyState === 2) this.setStatus(fileWithMeta, 'headers_received');
        if (xhr.readyState === 4) this.setStatus(fileWithMeta, 'done');
        this.emit();
      }

      if (xhr.status >= 400 && fileWithMeta.meta.status !== 'error_upload') {
        this.setStatus(fileWithMeta, 'error_upload');
        this.emit();
      }
    });

    formData.append('file', fileWithMeta.file);
    xhr.send(body || formData);
    Object.assign(fileWithMeta, { xhr });
    this.setStatus(fileWithMeta, 'uploading');
    this.emit();
  }
}
